/**
 * THE dirty rule for every settings form on the design-system SaveBar (D-27, MAR-372).
 *
 * A form is dirty when what Save WOULD WRITE differs from what is STORED — never when the form
 * differs from its own displayed baseline. The two are not the same thing: a form that shows a
 * default for a missing block, or normalises a stored value on display (an unsupported
 * `customerLimits.period` drawn as its fallback), displays a value the store does not hold. A
 * baseline built from the display is "clean" there, so the merchant could not persist the value
 * on screen without making a real edit — and reverting that edit made the bar clean again.
 *
 * So each form builds its payload with ONE function, which both `handleSave` and the dirty check
 * call, and compares the result here against the raw stored value (as read, or as last
 * successfully saved). A payload builder's deletions and migrations are therefore part of what
 * the check sees, by construction rather than by a parallel copy.
 *
 * The comparison is by JSON value, the way the PUT will carry it: object key order is ignored,
 * a key holding `undefined` is the same as an absent one (JSON drops both), and `null` is a
 * value — writing `null` where nothing was stored is a write.
 */

const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : canonical(v)))
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    return Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = canonical(obj[k])
        return acc
      }, {})
  }
  return value
}

/** True when writing `wouldWrite` would leave the stored value exactly as it is. */
export const matchesStored = (wouldWrite: unknown, stored: unknown): boolean =>
  JSON.stringify(canonical(wouldWrite)) === JSON.stringify(canonical(stored))
